import { useEffect, useState } from "react";
import { Bell, BellOff, BellRing } from "lucide-react";
import { toast } from "sonner";
import { currentPushSubscription, ensurePushSubscription, pushSupported } from "@/lib/pushClient";
import { invApi } from "./api";

/** Admin-only: subscribe THIS device to low-stock push alerts (email to owner is always on). */
export function LowStockAlertsCard({ token }) {
  const [state, setState] = useState("loading"); // loading | unsupported | off | on | busy
  const [devices, setDevices] = useState(0);
  const supported = pushSupported();

  useEffect(() => {
    if (!supported) { setState("unsupported"); return; }
    (async () => {
      try {
        const sub = await currentPushSubscription();
        const { data } = await invApi(token).get("/admin/push/status", { params: { endpoint: sub?.endpoint || "" } });
        setDevices(data.devices);
        setState(data.this_device ? "on" : "off");
      } catch { setState("off"); }
    })();
  }, [token, supported]);

  const enable = async () => {
    setState("busy");
    try {
      const sub = await ensurePushSubscription();
      await invApi(token).post("/admin/push/subscribe", { subscription: sub.toJSON(), label: navigator.userAgent.slice(0, 60) });
      setDevices((d) => d + 1);
      setState("on");
      toast.success("This device will get low-stock alerts");
    } catch (err) { toast.error(err?.response?.data?.detail || err?.message || "Couldn't enable alerts"); setState("off"); }
  };

  const disable = async () => {
    setState("busy");
    try {
      const sub = await currentPushSubscription();
      if (sub) { await invApi(token).delete("/admin/push/subscribe", { params: { endpoint: sub.endpoint } }); await sub.unsubscribe(); }
      setDevices((d) => Math.max(0, d - 1));
      setState("off");
      toast.success("Alerts off on this device");
    } catch { toast.error("Couldn't turn off"); setState("on"); }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-2xl px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="low-stock-alerts-card">
      <div className="flex-1 min-w-[180px]">
        <div className="font-black text-stone-800 text-sm uppercase tracking-widest flex items-center gap-2"><BellRing className="w-4 h-4 text-brand-red" /> Low-stock alerts</div>
        <div className="text-[11px] text-stone-500" data-testid="low-stock-alerts-status">
          Email to the owner is always on — one alert per item until it&apos;s restocked.
          {state === "unsupported" ? " Push isn't supported in this browser." : ` Push: ${devices} device${devices === 1 ? "" : "s"} subscribed${state === "on" ? " (incl. this one)" : ""}.`}
        </div>
      </div>
      {state !== "unsupported" && (
        state === "on" ? (
          <button type="button" onClick={disable} disabled={state === "busy"} data-testid="low-stock-alerts-disable"
            className="border-2 border-stone-200 text-stone-600 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-black flex items-center gap-1"><BellOff className="w-3.5 h-3.5" /> Turn off here</button>
        ) : (
          <button type="button" onClick={enable} disabled={state === "busy" || state === "loading"} data-testid="low-stock-alerts-enable"
            className="bg-brand-red text-white px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-black flex items-center gap-1 disabled:opacity-50"><Bell className="w-3.5 h-3.5" /> Enable push on this device</button>
        )
      )}
    </div>
  );
}
